/**
 * Utility functions for exporting and importing application configuration
 */

/**
 * Extracts current configuration from state for export
 * @param {Object} state - Current application state
 * @returns {Object} Configuration object ready for export
 */
export const extractConfig = (state) => {
  const {
    // Base configuration
    baseSuccessRate,
    location,
    
    // Core team salaries
    tristenSalaryTier,
    martinSalaryTier,
    
    // Designer configuration
    hireDesigner,
    designerSalary,
    designerHoursPerWeek,
    designerEmploymentType,
    designerPeriods,
    
    // Executive Assistant configuration
    eaType,
    eaSalary,
    eaHoursPerWeek,
    eaEmploymentType,
    eaPeriods,
    
    // Social Media Manager configuration
    hireSocial,
    socialSalary,
    socialHoursPerWeek,
    socialEmploymentType,
    socialPeriods,
    
    // Work environment and resources
    workEnvironment,
    operatingResources,
    
    // Additional costs
    legalCosts,
    travelCosts,
    additionalCosts,
  } = state;
  
  return {
    baseConfig: {
      baseSuccessRate,
      location,
    },
    coreTeam: {
      tristenSalaryTier,
      martinSalaryTier,
    },
    designer: {
      hire: hireDesigner,
      salary: designerSalary,
      hoursPerWeek: designerHoursPerWeek,
      employmentType: designerEmploymentType,
      periods: designerPeriods,
    },
    executiveAssistant: {
      type: eaType,
      salary: eaSalary,
      hoursPerWeek: eaHoursPerWeek,
      employmentType: eaEmploymentType,
      periods: eaPeriods,
    },
    socialMedia: {
      hire: hireSocial,
      salary: socialSalary,
      hoursPerWeek: socialHoursPerWeek,
      employmentType: socialEmploymentType,
      periods: socialPeriods,
    },
    environment: {
      workEnvironment,
      operatingResources,
    },
    additionalCosts: {
      legalCosts,
      travelCosts,
      additionalCosts,
    },
    meta: {
      version: "1.0.0",
      exportDate: new Date().toISOString(),
      applicationName: "hopingwegetfunded",
    }
  };
};

/**
 * Validates imported configuration data
 * @param {Object} config - Imported configuration object
 * @returns {Object} Validation result with status and message
 */
export const validateConfig = (config) => {
  // Check if config is valid JSON
  if (!config || typeof config !== 'object') {
    return { valid: false, message: "Invalid configuration format" };
  }
  
  // Check for required sections
  const requiredSections = [
    'baseConfig', 'coreTeam', 'designer', 'executiveAssistant', 
    'socialMedia', 'environment', 'additionalCosts', 'meta'
  ];
  
  for (const section of requiredSections) {
    if (!config[section]) {
      return { valid: false, message: `Missing required section: ${section}` };
    }
  }
  
  // Check version compatibility (for future use)
  if (config.meta && config.meta.version) {
    const version = config.meta.version.split('.');
    if (parseInt(version[0]) > 1) {
      return { 
        valid: false, 
        message: `Configuration version ${config.meta.version} is not compatible with this application` 
      };
    }
  }
  
  return { valid: true };
};

/**
 * Applies imported configuration to application state
 * @param {Object} config - Validated configuration object
 * @returns {Object} State updates to apply
 */
export const applyConfig = (config) => {
  const { valid } = validateConfig(config);
  if (!valid) {
    throw new Error("Cannot apply invalid configuration");
  }
  
  return {
    // Base configuration
    baseSuccessRate: config.baseConfig.baseSuccessRate,
    location: config.baseConfig.location,
    
    // Core team salaries
    tristenSalaryTier: config.coreTeam.tristenSalaryTier,
    martinSalaryTier: config.coreTeam.martinSalaryTier,
    
    // Designer configuration
    hireDesigner: config.designer.hire,
    designerSalary: config.designer.salary,
    designerHoursPerWeek: config.designer.hoursPerWeek,
    designerEmploymentType: config.designer.employmentType,
    designerPeriods: config.designer.periods,
    
    // Executive Assistant configuration
    eaType: config.executiveAssistant.type,
    eaSalary: config.executiveAssistant.salary,
    eaHoursPerWeek: config.executiveAssistant.hoursPerWeek,
    eaEmploymentType: config.executiveAssistant.employmentType,
    eaPeriods: config.executiveAssistant.periods,
    
    // Social Media Manager configuration
    hireSocial: config.socialMedia.hire,
    socialSalary: config.socialMedia.salary,
    socialHoursPerWeek: config.socialMedia.hoursPerWeek,
    socialEmploymentType: config.socialMedia.employmentType,
    socialPeriods: config.socialMedia.periods,
    
    // Work environment and resources
    workEnvironment: config.environment.workEnvironment,
    operatingResources: config.environment.operatingResources,
    
    // Additional costs
    legalCosts: config.additionalCosts.legalCosts,
    travelCosts: config.additionalCosts.travelCosts,
    additionalCosts: config.additionalCosts.additionalCosts,
  };
};

/**
 * Returns the default configuration
 * @returns {Object} Default configuration
 */
export const getDefaultConfig = () => {
  return {
    baseConfig: {
      baseSuccessRate: 10,
      location: 'stcharles',
    },
    coreTeam: {
      tristenSalaryTier: 1,
      martinSalaryTier: 1,
    },
    designer: {
      hire: false,
      salary: 70000,
      hoursPerWeek: 40,
      employmentType: 'employee',
      periods: [{
        id: 'designer-default',
        startMonth: 0,
        endMonth: 12,
        hoursPerWeek: 40,
        employmentType: 'employee'
      }],
    },
    executiveAssistant: {
      type: 'none',
      salary: 30000,
      hoursPerWeek: 40,
      employmentType: 'employee',
      periods: [{
        id: 'ea-default',
        startMonth: 0,
        endMonth: 12,
        hoursPerWeek: 40,
        employmentType: 'employee'
      }],
    },
    socialMedia: {
      hire: false,
      salary: 30000,
      hoursPerWeek: 40,
      employmentType: 'employee',
      periods: [{
        id: 'social-default',
        startMonth: 0,
        endMonth: 12,
        hoursPerWeek: 40,
        employmentType: 'employee'
      }],
    },
    environment: {
      workEnvironment: 'garage',
      operatingResources: 'sustainable',
    },
    additionalCosts: {
      legalCosts: 0,
      travelCosts: 0,
      additionalCosts: 0,
    },
    meta: {
      version: "1.0.0",
      exportDate: new Date().toISOString(),
      applicationName: "hopingwegetfunded",
    }
  };
};